import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { queryClient } from "@/index";
import { IntegrationScope } from "../integrations.types";
import {
  GoogleGetAuthUrlResponse,
  GoogleGetIntegrationResponse,
  GoogleGetUsersQuery,
  GoogleSaveOAuth2CredentialsData,
  GoogleSaveServiceAccountCredentialsData,
  GoogleUseGetInfiniteUsers,
  GoogleUseGetUsers,
} from "./Google.types";
import { GoogleAPI } from "./Google.api";

const GoogleQueryKeys = {
  integration: (query?: any) =>
    !!query ? ["google", "integration", query] : ["google", "integration"],
  authUrl: ["google", "authUrl"],
  authToken: ["google", "authToken"],
  users: (query?: GoogleGetUsersQuery) =>
    !!query ? ["google", "users", query] : ["google", "users"],
};

const useGetIntegration = (values: {
  query?: {};
  options?: Omit<UseQueryOptions, "queryKey" | "queryFn">;
}) => {
  return useQuery({
    queryKey: GoogleQueryKeys.integration(values.query),
    queryFn: () => GoogleAPI.getIntegration(values.query),
    ...values.options,
  }) as UseQueryResult<GoogleGetIntegrationResponse, Error>;
};

const resetQuery = () => {
  queryClient.resetQueries({
    queryKey: GoogleQueryKeys.integration(),
  });
};

const useGetAuthUrl = (options?: UseQueryOptions) => {
  return useQuery({
    queryKey: GoogleQueryKeys.authUrl,
    queryFn: GoogleAPI.getAuthUrl,
    ...options,
  }) as UseQueryResult<GoogleGetAuthUrlResponse, Error>;
};

const useGetUsers: GoogleUseGetUsers = (query, options) => {
  return useQuery({
    queryKey: GoogleQueryKeys.users(query),
    queryFn: () => GoogleAPI.getUsers(query),
    ...options,
  });
};

const useGetInfiniteUsers: GoogleUseGetInfiniteUsers = (query, options) => {
  return useInfiniteQuery({
    queryKey: GoogleQueryKeys.users(query),
    queryFn: async ({ pageParam }) => {
      const result = await GoogleAPI.getUsers({
        ...query,
        page: pageParam,
      });
      if (result?.success === false) throw new Error("Failed to fetch users");
      return result;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.nextPage;
    },
    initialPageParam: "",
    placeholderData: (data) => data,
    ...options,
  });
};

const useSaveServiceAccountCredentials = () => {
  return useMutation<
    {
      data: {
        integration_id: number;
      };
    },
    Error,
    GoogleSaveServiceAccountCredentialsData
  >({
    mutationFn: (values) => GoogleAPI.saveServiceAccountCredentials(values),
  });
};

const useSaveOAuth2Credentials = (
  values: GoogleSaveOAuth2CredentialsData,
  options?: Omit<UseQueryOptions, "queryKey" | "queryFn">
) => {
  return useQuery({
    queryKey: GoogleQueryKeys.authToken,
    queryFn: () => GoogleAPI.saveOAuth2Credentials(values),
    ...options,
  });
};

export const GoogleQuery = {
  GoogleQueryKeys,
  useGetIntegration,
  resetQuery,
  useGetAuthUrl,
  useSaveOAuth2Credentials,
  useGetUsers,
  useGetInfiniteUsers,
  useSaveServiceAccountCredentials,
};
